import { createContext, FC, ReactNode, useCallback, useDeferredValue, useLayoutEffect, useMemo, useState } from "react"

import * as RToast from "@radix-ui/react-toast"

import ErrorBoundary from "@/components/ErrorBoundary"
import Toast, { type ToastProps } from "@/components/Toast"

export const ToastContext = createContext({
  addToast: (_content: ToastProps["content"], _type?: ToastProps["type"]) => {},
})

const ToastErrorFallback: FC<{ children: ReactNode }> = ({ children }) => {
  useLayoutEffect(() => {
    console.error("An error occurred in the ToastContextProvider and an error toast could not be shown.")
  }, [])
  return children
}

export const ToastContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<({ timestamp: number } & ToastProps)[]>([])
  const addToast = useCallback(
    (content: ToastProps["content"] & { permanent?: boolean }, type: ToastProps["type"] = "success") => {
      setToasts((t) => [...t, { content, permanent: content?.permanent, type, timestamp: Date.now() }])
    },
    []
  )
  const deferredToasts = useDeferredValue(toasts)

  const fields = useMemo(() => ({ addToast }), [addToast])

  return (
    <ErrorBoundary fallback={<ToastErrorFallback>{children}</ToastErrorFallback>}>
      <RToast.Provider duration={4_000}>
        <ToastContext.Provider value={fields}>
          {children}
          <RToast.Viewport className="pointer-events-none fixed top-0 right-0 z-50 flex w-full flex-col items-center gap-2 p-2" />
          {deferredToasts.length > 0 &&
            deferredToasts.map(({ timestamp, ...toast }, idx) => (
              <Toast
                key={timestamp}
                className="pointer-events-auto"
                {...toast}
                close={() => setToasts((t) => t.filter((_v, idz) => idx !== idz))}
              />
            ))}
        </ToastContext.Provider>
      </RToast.Provider>
    </ErrorBoundary>
  )
}
export default ToastContext
